/**
 *  @file Composable for resizing post text to fit in the viewport for slideshow
 */

import { hasCustomProperties } from '@@/bits/post_properties'
import { BODY, CUSTOM_PROPERTIES_LABEL, CUSTOM_PROPERTIES_VALUE, HEADING } from '@@/bits/slideshow_classnames'
import { tailwindFullConfig } from '@@/bits/tailwind_config'
import {
  isSmallerThanDesktop,
  isSmallerThanDesktop2XL,
  isSmallerThanDesktop3XL,
  isSmallerThanTabletLandscape,
  isSmallerThanTabletPortrait,
} from '@@/bits/window'
import type { Post } from '@@/types'
import { isEmpty } from 'es-toolkit/compat'
import type { Ref } from 'vue'
import { onUnmounted, ref, watch, watchEffect } from 'vue'

enum TEXT_SIZE {
  xs = 'xs',
  sm = 'sm',
  md = 'md',
  lg = 'lg',
  xl = 'xl',
  '2xl' = '2xl',
}
const TEXT_SIZE_STEP_DOWN_ORDER = {
  HEADING: {
    MOBILE: [TEXT_SIZE.sm],
    TABLET_PORTRAIT_OR_LARGER: [TEXT_SIZE.md, TEXT_SIZE.lg, TEXT_SIZE.xl, TEXT_SIZE['2xl']],
  },
  BODY: {
    MOBILE: [TEXT_SIZE.xs, TEXT_SIZE.sm],
    TABLET_PORTRAIT_OR_LARGER: [TEXT_SIZE.sm, TEXT_SIZE.md, TEXT_SIZE.lg, TEXT_SIZE.xl, TEXT_SIZE['2xl']],
  },
  CUSTOM_PROPERTIES_LABEL: {
    MOBILE: [TEXT_SIZE.xs],
    TABLET_PORTRAIT_OR_LARGER: [TEXT_SIZE.xs, TEXT_SIZE.sm, TEXT_SIZE.md, TEXT_SIZE.lg, TEXT_SIZE.xl],
  },
  CUSTOM_PROPERTIES_VALUE: {
    MOBILE: [TEXT_SIZE.xs, TEXT_SIZE.sm],
    TABLET_PORTRAIT_OR_LARGER: [TEXT_SIZE.sm, TEXT_SIZE.md, TEXT_SIZE.lg, TEXT_SIZE.xl, TEXT_SIZE['2xl']],
  },
}
type TextType = keyof typeof TEXT_SIZE_STEP_DOWN_ORDER
type SlideshowClassnames =
  | typeof BODY
  | typeof HEADING
  | typeof CUSTOM_PROPERTIES_LABEL
  | typeof CUSTOM_PROPERTIES_VALUE

interface Params {
  postContainerRef: Ref<HTMLDivElement | undefined>
  postTextContainerRef: Ref<HTMLDivElement | undefined>
  postBodyWithCustomPropertiesContainer: Ref<HTMLDivElement | undefined>
  postBodyRef: Ref<HTMLDivElement | undefined>
  postSubjectRef: Ref<HTMLDivElement | undefined>
  postAttachmentRef: Ref<HTMLDivElement | undefined>
  currentPost: Ref<Post>
  isAttachmentLoaded: Ref<boolean>
  isCurrentlyViewed: Ref<boolean>
  customPropertyLabelRefs: Ref<HTMLElement[]>
  customPropertyValueRefs: Ref<HTMLElement[]>
}

export function useSlideshowResizePostTextV2({
  postContainerRef,
  postTextContainerRef,
  postBodyWithCustomPropertiesContainer,
  postBodyRef,
  postSubjectRef,
  postAttachmentRef,
  currentPost,
  isAttachmentLoaded,
  isCurrentlyViewed,
  customPropertyLabelRefs,
  customPropertyValueRefs,
}: Params): {
  shouldShowScrollButton: Ref<boolean>
  scrollDownPostText: () => void
} {
  // =========================
  // GENERAL LOGIC
  // =========================

  const hasRunInitialSetup = ref(false)
  const isScrollable = ref(false)

  // Used in place of onMounted because initial resizePostText should only be called when all the relevant DOM elements have loaded
  watchEffect(() => {
    if (hasRunInitialSetup.value) return

    const hasNoAttachment = isEmpty(currentPost.value.attachment)
    const hasNoBody = isEmpty(currentPost.value.body)
    const hasNoSubject = isEmpty(currentPost.value.subject)

    const hasPostContainerLoaded = postContainerRef.value != null
    const hasPostBodyLoadedIfAny = postBodyRef.value != null
    const hasPostSubjectLoadedIfAny = postSubjectRef.value != null
    // Need to wait for attachment to load otherwise post text resize logic will not take attachment height into account for overflow
    const hasAttachmentLoadedIfAny = isAttachmentLoaded.value

    const haveAllRelevantElementsLoaded =
      hasPostContainerLoaded &&
      (hasNoAttachment || hasAttachmentLoadedIfAny) &&
      (hasNoBody || hasPostBodyLoadedIfAny) &&
      (hasNoSubject || hasPostSubjectLoadedIfAny)
    const hasNoPostText = hasNoSubject && hasNoBody

    if (haveAllRelevantElementsLoaded) {
      hasRunInitialSetup.value = true
      if (hasNoPostText) {
        return
      }

      resizePostText()
      window.addEventListener('resize', resizePostText)
    }
  })

  onUnmounted(() => {
    window.removeEventListener('resize', resizePostText)
  })

  const resizePostText = (): void => {
    resetPostStyles()

    if (isPostContentOverflowingContainer()) {
      stepDownPostTextToFit()
    }

    // Stepping down post text may not be enough to prevent overflow
    if (isPostContentOverflowingContainer()) {
      allowPostTextScrolling()
    } else {
      // This must be called after stepping down post text,
      // since the step down process can reduce the text to a single line.
      // Also, there shouldn't be a case where we need text centering and scrolling together.
      centerTextIfSingleLine()
    }
  }

  const resetPostStyles = (): void => {
    const defaultTextSize = getDefaultTextSizeForWindow()
    currentBodySize.value = defaultTextSize
    currentHeadingSize.value = defaultTextSize
    currentCustomPropertyLabelSize.value = getDefaultCustomPropertyLabelSizeForWindow()
    currentCustomPropertyValueSize.value = defaultTextSize

    revertStepDownPostText()
    revertAllowPostTextScrolling()
    revertCenterTextIfSingleLine()
  }

  // =========================
  // DIMENSION CALCULATION HELPERS
  // =========================

  // Having no attachment is effectively single column because there's only text
  const isSingleColumnLayout = (): boolean => isSmallerThanTabletLandscape() || postAttachmentRef.value == null

  const getPostContainerInnerWidth = (): number => {
    if (postContainerRef.value == null) return 0
    const postContainerStyle = window.getComputedStyle(postContainerRef.value)
    return (
      postContainerRef.value.clientWidth -
      parseFloat(postContainerStyle.paddingLeft) -
      parseFloat(postContainerStyle.paddingRight)
    )
  }

  const getPostContainerInnerHeight = (): number => {
    if (postContainerRef.value == null) return 0
    const postContainerStyle = window.getComputedStyle(postContainerRef.value)
    return (
      postContainerRef.value.clientHeight -
      parseFloat(postContainerStyle.paddingTop) -
      parseFloat(postContainerStyle.paddingBottom)
    )
  }

  /**
   * Visual diagram: https://whimsical.com/text-resizing-logic-BoH6npYJaDyJd2PMxhfV5u@2Ux7TurymLWWFpGhWmGT
   *
   * This relies on overflow-hidden not being set on any of the containers we read the height from.
   */
  const getPostContainerOverflowHeight = (): number => {
    if (postTextContainerRef.value == null || postContainerRef.value == null) return 0

    if (isSingleColumnLayout()) {
      // We set justifyContent 'start' because 'center' (the default) can cause the scrolling area to overflow past the top of the container,
      // and scrollHeight doesn't include any height over the top edge
      // Reference: https://www.w3.org/TR/cssom-view-1/#dom-element-scrollheight
      postContainerRef.value.style.justifyContent = 'start'

      // This could be simplified to postContainerRef.scrollHeight - postContainerRef.clientHeight,
      // but on Firefox, somehow scrollHeight is always constrained to clientHeight, i.e. scrollHeight - clientHeight = 0
      const postContainerClientHeight = postContainerRef.value?.clientHeight
      const postTextContainerScrollHeight = postTextContainerRef.value?.scrollHeight
      const postAttachmentScrollHeight = postAttachmentRef.value?.scrollHeight ?? 0
      const overflowHeight = postTextContainerScrollHeight + postAttachmentScrollHeight - postContainerClientHeight

      // Now that calculation has finished, we can reset the style
      postContainerRef.value.style.justifyContent = ''

      return overflowHeight
    }

    // Side by side layout
    return postTextContainerRef.value.scrollHeight - getPostContainerInnerHeight()
  }

  const isPostContentOverflowingContainer = (): boolean => {
    return getPostContainerOverflowHeight() > 0
  }

  const getPostTextContainerClientHeight = (): number => {
    if (postTextContainerRef.value == null || postContainerRef.value == null) return 0
    const postTextContainerStyle = window.getComputedStyle(postTextContainerRef.value)

    if (isSingleColumnLayout()) {
      const postAttachmentHeight = postAttachmentRef.value?.clientHeight ?? 0

      return (
        getPostContainerInnerHeight() -
        parseFloat(postTextContainerStyle.paddingTop) -
        parseFloat(postTextContainerStyle.paddingBottom) -
        postAttachmentHeight
      )
    }

    return (
      getPostContainerInnerHeight() -
      parseFloat(postTextContainerStyle.paddingTop) -
      parseFloat(postTextContainerStyle.paddingBottom)
    )
  }

  // =========================
  // TEXT CENTERING LOGIC
  // =========================

  const revertCenterTextIfSingleLine = (): void => {
    if (postBodyRef.value != null) {
      postBodyRef.value.style.textAlign = ''
    }
    if (postSubjectRef.value != null) {
      postSubjectRef.value.style.textAlign = ''
    }
  }

  /**
   * Assumptions:
   * - postTextContainerRef uses fit-content, we can compare the widths
   *  as a proxy for whether the text has gone beyond a single line or not
   * - We only center text for single column layout, side by side layout must always be start aligned
   */
  const centerTextIfSingleLine = (): void => {
    if (!isSingleColumnLayout() || hasCustomProperties(currentPost.value)) return
    if (postTextContainerRef.value == null || postContainerRef.value == null) return

    const postTextContainerWidth = postTextContainerRef.value.clientWidth
    const postTextContainerMaxWidth = getPostContainerInnerWidth()
    const isSingleLine = postTextContainerWidth < postTextContainerMaxWidth

    if (!isSingleLine) return

    const postBodyDoesNotHaveListElements = postTextContainerRef.value.querySelector('ul, ol') == null
    if (postBodyRef.value != null && postBodyDoesNotHaveListElements) {
      postBodyRef.value.style.textAlign = 'center'
    }
    if (postSubjectRef.value != null) {
      postSubjectRef.value.style.textAlign = 'center'
    }
  }

  // =========================
  // STEP DOWN LOGIC
  // =========================

  const getDefaultTextSizeForWindow = (): TEXT_SIZE => {
    if (isSmallerThanTabletPortrait()) {
      return TEXT_SIZE.sm
    }
    if (isSmallerThanTabletLandscape()) {
      return TEXT_SIZE.md
    }
    if (isSmallerThanDesktop()) {
      return TEXT_SIZE.lg
    }
    if (isSmallerThanDesktop2XL()) {
      return TEXT_SIZE.xl
    }
    return TEXT_SIZE['2xl']
  }

  const getDefaultCustomPropertyLabelSizeForWindow = (): TEXT_SIZE => {
    if (isSmallerThanTabletPortrait()) {
      return TEXT_SIZE.xs
    }
    if (isSmallerThanTabletLandscape()) {
      return TEXT_SIZE.sm
    }
    if (isSmallerThanDesktop()) {
      return TEXT_SIZE.md
    }
    if (isSmallerThanDesktop2XL()) {
      return TEXT_SIZE.lg
    }
    return TEXT_SIZE.xl
  }

  const currentBodySize = ref<TEXT_SIZE>(getDefaultTextSizeForWindow())
  const currentHeadingSize = ref<TEXT_SIZE>(getDefaultTextSizeForWindow())

  const currentCustomPropertyLabelSize = ref<TEXT_SIZE>(getDefaultCustomPropertyLabelSizeForWindow())
  const currentCustomPropertyValueSize = ref<TEXT_SIZE>(getDefaultTextSizeForWindow())

  const getCurrentTextSizeRef = (textType: TextType): Ref<TEXT_SIZE> => {
    switch (textType) {
      case 'HEADING':
        return currentHeadingSize
      case 'BODY':
        return currentBodySize
      case 'CUSTOM_PROPERTIES_LABEL':
        return currentCustomPropertyLabelSize
      case 'CUSTOM_PROPERTIES_VALUE':
        return currentCustomPropertyValueSize
      default:
        throw new Error(`Unrecognized text type: ${String(textType)}`)
    }
  }

  const getNextSmallestTextSize = (textType: TextType): TEXT_SIZE | null => {
    const stepDownOrder = isSmallerThanTabletPortrait()
      ? TEXT_SIZE_STEP_DOWN_ORDER[textType].MOBILE
      : TEXT_SIZE_STEP_DOWN_ORDER[textType].TABLET_PORTRAIT_OR_LARGER

    const currentTextSize = getCurrentTextSizeRef(textType)

    const currentTextSizeIndex = stepDownOrder.indexOf(currentTextSize.value)
    if (currentTextSizeIndex <= 0) return null

    const nextSmallestTextSizeIndex = currentTextSizeIndex - 1
    const nextSmallestTextSize = stepDownOrder[nextSmallestTextSizeIndex]
    return nextSmallestTextSize
  }

  const revertStepDownPostText = (): void => {
    if (postBodyRef.value != null) {
      postBodyRef.value.style.fontSize = ''
      postBodyRef.value.style.lineHeight = ''
      postBodyRef.value.style.letterSpacing = ''
    }
    if (postSubjectRef.value != null) {
      postSubjectRef.value.style.fontSize = ''
      postSubjectRef.value.style.lineHeight = ''
      postSubjectRef.value.style.letterSpacing = ''
    }
    if (customPropertyLabelRefs.value != null) {
      customPropertyLabelRefs.value.forEach((labelRef) => {
        if (labelRef == null) return
        labelRef.style.fontSize = ''
        labelRef.style.lineHeight = ''
        labelRef.style.letterSpacing = ''
      })
    }
    if (customPropertyValueRefs.value != null) {
      customPropertyValueRefs.value.forEach((valueRef) => {
        if (valueRef == null) return
        valueRef.style.fontSize = ''
        valueRef.style.lineHeight = ''
        valueRef.style.letterSpacing = ''
      })
    }
  }

  const stepDownPostTextToFit = (): void => {
    stepDownPostText(postBodyRef, 'BODY')
    stepDownCustomProperties()

    while (isPostContentOverflowingContainer()) {
      const hasPostBodyFinishedSteppingDown = stepDownPostText(postBodyRef, 'BODY')
      const hasPostSubjectFinishedSteppingDown = stepDownPostText(postSubjectRef, 'HEADING')
      const hasPostCustomPropertiesFinishedSteppingDown = stepDownCustomProperties()

      if (
        hasPostBodyFinishedSteppingDown &&
        hasPostSubjectFinishedSteppingDown &&
        hasPostCustomPropertiesFinishedSteppingDown
      ) {
        break
      }
    }
  }

  const getClassesForTextType = (textType: TextType): SlideshowClassnames => {
    switch (textType) {
      case 'HEADING':
        return HEADING
      case 'BODY':
        return BODY
      case 'CUSTOM_PROPERTIES_LABEL':
        return CUSTOM_PROPERTIES_LABEL
      case 'CUSTOM_PROPERTIES_VALUE':
        return CUSTOM_PROPERTIES_VALUE
      default:
        throw new Error(`Unrecognized type: ${String(textType)}`)
    }
  }

  /**
   * - Returns `true` if text has stepped down to minimum (or text doesn't exist)
   * - Returns `false` otherwise
   */
  const stepDownCustomProperties = (): boolean => {
    if (!hasCustomProperties(currentPost.value)) return true

    const labelStepDownResults = stepDownPostText(customPropertyLabelRefs, 'CUSTOM_PROPERTIES_LABEL')
    const valueStepDownResults = stepDownPostText(customPropertyValueRefs, 'CUSTOM_PROPERTIES_VALUE')

    // step down labels and values together; stop once any of them are finished.
    return labelStepDownResults || valueStepDownResults
  }

  /**
   * - Returns `true` if text has stepped down to minimum (or text doesn't exist)
   * - Returns `false` otherwise
   */
  const stepDownPostText = (
    textRef: Ref<HTMLDivElement | undefined> | Ref<HTMLElement[]>,
    textType: TextType,
  ): boolean => {
    const nextSmallestTextSize = getNextSmallestTextSize(textType)
    if (textRef.value == null || nextSmallestTextSize == null) {
      return true
    }
    const classesForTextType = getClassesForTextType(textType)
    const nextSmallestClassname = classesForTextType[nextSmallestTextSize]
    if (nextSmallestClassname != null) {
      const [fontSize, { lineHeight, letterSpacing }] = tailwindFullConfig?.theme?.fontSize?.[nextSmallestClassname]
      if (Array.isArray(textRef.value)) {
        textRef.value.forEach((el) => {
          el.style.fontSize = fontSize
          el.style.lineHeight = lineHeight
          el.style.letterSpacing = letterSpacing
        })
      } else {
        textRef.value.style.fontSize = fontSize
        textRef.value.style.lineHeight = lineHeight
        textRef.value.style.letterSpacing = letterSpacing
      }
    }

    const currentTextSizeRef = getCurrentTextSizeRef(textType)
    currentTextSizeRef.value = nextSmallestTextSize

    return false
  }

  // =========================
  // SCROLL LOGIC
  // =========================

  const scrollableContainerRef = ref<HTMLDivElement>()
  const shouldShowScrollButton = ref(false)

  const getMaskImageGradientHeight = (): number => {
    if (isSmallerThanTabletPortrait()) {
      return 24
    }
    if (isSmallerThanDesktop()) {
      return 32
    }
    if (isSmallerThanDesktop2XL()) {
      return 48
    }
    if (isSmallerThanDesktop3XL()) {
      return 64
    }
    return 80
  }

  const getHasScrolledToBottom = (): boolean => {
    if (scrollableContainerRef.value == null) return false

    // NOTE: scrollTop can be fractional, while scrollHeight and clientHeight are rounded integers,
    // so (scrollHeight - clientHeight - scrollTop) may not always be 0 (which is usually used to determine if user has scrolled to bottom).
    // E.g. 100 (scrollHeight) - 50 (clientHeight) - 49.5 (scrollTop) = 0.5
    return (
      Math.abs(
        scrollableContainerRef.value.scrollHeight -
          scrollableContainerRef.value.clientHeight -
          scrollableContainerRef.value.scrollTop,
      ) < 1
    )
  }

  function addFadeToScrollableContainer(): void {
    if (scrollableContainerRef.value == null) return

    const hasScrolledtoBottom = getHasScrolledToBottom()
    const fadePosition = hasScrolledtoBottom ? 'top' : 'bottom'
    const gradientHeight = getMaskImageGradientHeight()
    scrollableContainerRef.value.style.webkitMaskImage = `linear-gradient(
          to ${fadePosition},
          rgba(0, 0, 0, 1) calc(100% - ${gradientHeight}px),
          rgba(0, 0, 0, 0) 100%
          )
          `
  }

  function displayScrollButtonBasedOnScrollPosition(): void {
    const hasScrolledtoBottom = getHasScrolledToBottom()
    shouldShowScrollButton.value = !hasScrolledtoBottom
  }

  const revertAllowPostTextScrolling = (): void => {
    if (scrollableContainerRef.value != null) {
      scrollableContainerRef.value.removeEventListener('scroll', addFadeToScrollableContainer)
      scrollableContainerRef.value.removeEventListener('scroll', displayScrollButtonBasedOnScrollPosition)
      scrollableContainerRef.value.style.height = ''
      scrollableContainerRef.value.style.overflowY = ''
      scrollableContainerRef.value.style.webkitMaskImage = ''
    }

    if (postTextContainerRef.value != null) {
      postTextContainerRef.value.style.marginTop = ''
      postTextContainerRef.value.style.marginBottom = ''
      postTextContainerRef.value.style.paddingTop = ''
      postTextContainerRef.value.style.paddingBottom = ''
    }

    shouldShowScrollButton.value = false
    isScrollable.value = false
  }

  const allowPostTextScrolling = (): void => {
    if (postTextContainerRef.value == null) return

    const postTextContainerClientHeight = getPostTextContainerClientHeight()
    const postSubjectScrollHeight = postSubjectRef.value?.scrollHeight ?? 0
    const shouldEntireContainerBeScrollable = postSubjectScrollHeight / postTextContainerClientHeight >= 0.25

    if (shouldEntireContainerBeScrollable) {
      postTextContainerRef.value.style.height = `${postTextContainerClientHeight}px`
      postTextContainerRef.value.style.overflowY = 'scroll'

      scrollableContainerRef.value = postTextContainerRef.value
    } else {
      if (postBodyWithCustomPropertiesContainer.value == null) return
      const postTextContainerStyle = window.getComputedStyle(postTextContainerRef.value)
      const postSubjectClientHeight = postSubjectRef.value?.clientHeight ?? 0
      const gapBetweenSubjectAndBody = parseFloat(postTextContainerStyle.gap)
      const postBodyHeight = postTextContainerClientHeight - postSubjectClientHeight - gapBetweenSubjectAndBody

      // postBodyHeight can be negative for very short windows which breaks the layout because it's not a valid value
      postBodyWithCustomPropertiesContainer.value.style.height = `${Math.max(postBodyHeight, 0)}px`
      postBodyWithCustomPropertiesContainer.value.style.overflowY = 'scroll'

      scrollableContainerRef.value = postBodyWithCustomPropertiesContainer.value
    }

    addFadeToScrollableContainer()
    showScrollButtonIfValid()

    scrollableContainerRef.value.addEventListener('scroll', addFadeToScrollableContainer)
    scrollableContainerRef.value.addEventListener('scroll', displayScrollButtonBasedOnScrollPosition)

    // Padding breaks scroll behavior, so replace it with margin instead using the same values
    // An alternative would be changing the style on postTextContainerRef from padding to margin
    // but that would require changing a lot of the overflow calculation logic
    const postTextContainerStyle = window.getComputedStyle(postTextContainerRef.value)
    postTextContainerRef.value.style.marginTop = postTextContainerStyle.paddingTop
    postTextContainerRef.value.style.marginBottom = postTextContainerStyle.paddingTop
    postTextContainerRef.value.style.paddingTop = '0px'
    postTextContainerRef.value.style.paddingBottom = '0px'

    isScrollable.value = true
  }

  const scrollDownPostText = (): void => {
    if (scrollableContainerRef.value == null) return

    const height = scrollableContainerRef.value.clientHeight - getMaskImageGradientHeight()
    scrollableContainerRef.value.scrollBy({ top: height, behavior: 'smooth' })
    showScrollButtonIfValid()
  }

  const showScrollButtonIfValid = (): void => {
    const hasScrolledtoBottom = getHasScrolledToBottom()
    // We don't show the button on mobile because there isn't enough space
    if (hasScrolledtoBottom || isSmallerThanTabletPortrait()) {
      shouldShowScrollButton.value = false
      return
    }

    shouldShowScrollButton.value = true
  }

  /**
   * The scroll elements can behave weirdly when:
   * - A user has scrolled down on slide A
   * - Navigates to slide B
   * - Navigates back to slide A
   *
   * "Refresh" the styles to prevent this from happening
   */
  watch(isCurrentlyViewed, () => {
    if (isCurrentlyViewed.value && isScrollable.value) {
      addFadeToScrollableContainer()
      showScrollButtonIfValid()
    }
  })

  return {
    shouldShowScrollButton,
    scrollDownPostText,
  }
}
